import { ReactFlowProps } from 'reactflow';

/**
 * Memory-optimized React Flow configuration to prevent memory leaks
 */
export const MEMORY_OPTIMIZED_REACTFLOW_CONFIG: Partial<ReactFlowProps> = {
	// Limit the number of nodes that can be rendered at once
	maxZoom: 2,
	minZoom: 0.1,

	// Optimize rendering performance
	nodesDraggable: true,
	nodesConnectable: true,
	elementsSelectable: true,

	// Prevent unnecessary re-renders
	snapToGrid: false,
	snapGrid: [15, 15],

	// Connection line configuration to reduce rendering overhead
	connectionLineType: 'smoothstep',

	// Fit view options to prevent excessive calculations
	fitViewOptions: {
		padding: 0.1,
		includeHiddenNodes: false,
		maxZoom: 1.2,
		duration: 200, // Shorter animation duration
	},

	// Default edge options for better performance
	defaultEdgeOptions: {
		type: 'smoothstep',
		animated: false, // Disable animations to reduce CPU/memory usage
	},

	// Prevent excessive node position calculations
	nodeExtent: [
		[-5000, -5000],
		[5000, 5000],
	],

	// Optimize selection behavior
	selectNodesOnDrag: false,

	// Connection mode for better UX without performance impact
	connectionMode: 'loose' as const,

	// Disable pan on scroll for better performance on large workflows
	panOnScroll: true,
	panOnScrollMode: 'horizontal' as const,
	panOnScrollSpeed: 0.5,

	// Zoom configuration
	zoomOnScroll: true,
	zoomOnPinch: true,
	zoomOnDoubleClick: false, // Disable to prevent accidental zoom

	// Prevent drag on selection for better performance
	preventScrolling: true,

	// Delete key configuration
	deleteKeyCode: ['Delete', 'Backspace'],
	multiSelectionKeyCode: ['Meta', 'Control'],
};

/**
 * Performance monitoring configuration for React Flow
 */
export const REACTFLOW_PERFORMANCE_CONFIG = {
	// Monitor these metrics
	maxNodes: 100,
	maxEdges: 200,
	maxUndoStackSize: 50,

	// Performance thresholds
	renderTimeThreshold: 100, // ms
	memoryThreshold: 500, // MB

	// Cleanup intervals
	cleanupInterval: 30000, // 30 seconds
	memoryCheckInterval: 10000, // 10 seconds
};

/**
 * Memory cleanup utilities for React Flow
 */
export const ReactFlowMemoryUtils = {
	/**
	 * Clean up React Flow instance to prevent memory leaks
	 */
	cleanup: (instance: any) => {
		if (instance) {
			// Clear any stored references
			instance.setNodes([]);
			instance.setEdges([]);

			// Force a viewport reset
			instance.setViewport({ x: 0, y: 0, zoom: 1 });
		}
	},

	/**
	 * Optimize node data to reduce memory footprint
	 */
	optimizeNodeData: (nodes: any[]) => {
		return nodes.map(node => ({
			...node,
			data: {
				...node.data,
				// Remove large data objects that aren't needed for rendering
				config: node.data.config
					? {
							...node.data.config,
							// Keep only essential config data
					  }
					: {},
			},
		}));
	},

	/**
	 * Check if workflow exceeds performance thresholds
	 */
	checkPerformanceThresholds: (nodes: any[], edges: any[]) => {
		const warnings = [];

		if (nodes.length > REACTFLOW_PERFORMANCE_CONFIG.maxNodes) {
			warnings.push(
				`Too many nodes: ${nodes.length} (max: ${REACTFLOW_PERFORMANCE_CONFIG.maxNodes})`
			);
		}

		if (edges.length > REACTFLOW_PERFORMANCE_CONFIG.maxEdges) {
			warnings.push(
				`Too many edges: ${edges.length} (max: ${REACTFLOW_PERFORMANCE_CONFIG.maxEdges})`
			);
		}

		return warnings;
	},
};
